import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { formatMoney } from '../lib/format.js'
import KitchenLoader from './KitchenLoader.jsx'

// AI Waste Pattern Insights — sits at the top of the Waste tab.
// Click-to-generate. Pulls the last 14 days of waste logs from
// Supabase, builds a compact aggregated snapshot, and fires
// analyze-waste-patterns. Same UX shape as the daily / weekly briefs.
//
// Idle by default; manager taps once to look for patterns. Costs
// roughly a penny per click.
export default function WastePatternsCard({
  // Pulled from useWasteData — passed in so we don't double-fetch.
  todayTotal,
  yesterdayTotal,
  dailyTarget,
}) {
  const [phase, setPhase] = useState('idle') // idle | loading-data | generating | ready | error
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  // Reset phase if the underlying data changes meaningfully (e.g.,
  // the manager logs a new entry after generating). We don't auto-fire,
  // but we do mark the result as "stale" so the manager knows to
  // refresh.
  const dataKey = `${todayTotal}|${yesterdayTotal}|${dailyTarget}`
  const initialKey = useMemo(() => dataKey, []) // capture at first render
  const stale = phase === 'ready' && dataKey !== initialKey

  async function generate() {
    if (phase === 'generating' || phase === 'loading-data') return

    setPhase('loading-data')
    setError(null)

    try {
      // Pull last 14 days of waste_logs. RLS filters to current restaurant.
      const since = new Date()
      since.setDate(since.getDate() - 14)
      const { data: logs, error: qErr } = await supabase
        .from('waste_logs')
        .select('item_name, quantity, unit, total_cost, reason, logged_at')
        .gte('logged_at', since.toISOString())
        .order('logged_at', { ascending: false })
        .limit(500)
      if (qErr) throw qErr

      const safeLogs = logs ?? []

      // Tiny-data guard: skip the API call if there's barely anything to
      // analyze. Show a friendly nudge instead of asking the model to
      // invent a narrative for 2 entries.
      if (safeLogs.length < 5) {
        setResult({
          headline: `Only ${safeLogs.length} waste ${safeLogs.length === 1 ? 'entry' : 'entries'} logged in the last 14 days — not enough yet for pattern analysis.`,
          patterns: [],
          mood: 'neutral',
          summary_action: 'Log waste consistently for a week or two and tap this again — patterns emerge once there\'s a baseline of activity.',
          _too_thin: true,
        })
        setPhase('ready')
        return
      }

      // Build aggregations the model can reason over without re-deriving.
      const byItem = new Map()
      const byReason = new Map()
      const byDow = new Map()
      let totalDollars = 0
      for (const l of safeLogs) {
        const cost = Number(l.total_cost || 0)
        totalDollars += cost
        // by item
        const it = byItem.get(l.item_name) || { count: 0, total_cost: 0, total_qty: 0, unit: l.unit }
        it.count += 1
        it.total_cost += cost
        it.total_qty += Number(l.quantity || 0)
        byItem.set(l.item_name, it)
        // by reason
        const r = l.reason || '(none)'
        const rs = byReason.get(r) || { count: 0, total_cost: 0 }
        rs.count += 1
        rs.total_cost += cost
        byReason.set(r, rs)
        // by day-of-week
        const dow = new Date(l.logged_at).toLocaleDateString('en-US', { weekday: 'short' })
        const ds = byDow.get(dow) || { count: 0, total_cost: 0 }
        ds.count += 1
        ds.total_cost += cost
        byDow.set(dow, ds)
      }

      const top_items = [...byItem.entries()]
        .map(([name, v]) => ({ name, ...v, total_cost: round2(v.total_cost) }))
        .sort((a, b) => b.total_cost - a.total_cost)
        .slice(0, 8)

      const reason_breakdown = [...byReason.entries()]
        .map(([reason, v]) => ({ reason, count: v.count, total_cost: round2(v.total_cost) }))
        .sort((a, b) => b.total_cost - a.total_cost)

      const day_of_week = [...byDow.entries()]
        .map(([dow, v]) => ({ dow, count: v.count, total_cost: round2(v.total_cost) }))
        .sort((a, b) => b.total_cost - a.total_cost)

      const snapshot = {
        period_days: 14,
        total_entries: safeLogs.length,
        total_dollars: round2(totalDollars),
        avg_per_day: round2(totalDollars / 14),
        daily_target_dollars: Number(dailyTarget || 0),
        today_dollars: Number(todayTotal || 0),
        yesterday_dollars: Number(yesterdayTotal || 0),
        top_items,
        reason_breakdown,
        day_of_week,
      }

      setPhase('generating')

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/analyze-waste-patterns`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify({ snapshot }),
      })

      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data.error) {
        setError(data.message || 'AI could not analyze patterns.')
        setPhase('error')
        return
      }
      setResult(data)
      setPhase('ready')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[WastePatternsCard] generate failed', e)
      setError('Could not analyze — please try again in a moment.')
      setPhase('error')
    }
  }

  const tone = TONES[result?.mood] || TONES.neutral

  return (
    <section
      className={`mx-4 mt-4 rounded-2xl border p-4 shadow-sm ${tone.border} ${tone.bg}`}
      aria-label="AI waste patterns"
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className={`text-[11px] font-medium ${tone.label}`}>
          ✨ AI waste patterns
        </div>
        {phase === 'ready' && (
          <button
            type="button"
            onClick={generate}
            className="rounded-md px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
          >
            {stale ? 'Refresh' : 'Re-run'}
          </button>
        )}
      </div>

      {phase === 'idle' && (
        <div className="mt-2">
          <p className="text-sm text-black/75 leading-snug">
            Analyze the last 14 days of waste logs for patterns — items wasted
            most, day-of-week skews, and what to act on this week.
          </p>
          <button
            type="button"
            onClick={generate}
            className="mt-3 w-full rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
          >
            ✨ Find patterns
          </button>
          <div className="mt-1.5 text-[11px] text-black/50">
            Run it weekly after a few days of logging.
          </div>
        </div>
      )}

      {(phase === 'loading-data' || phase === 'generating') && (
        <div className="mt-2">
          <KitchenLoader
            align="row"
            size="sm"
            messages={
              phase === 'loading-data'
                ? ['Pulling 14 days of waste logs\u2026']
                : [
                    'Sorting through the waste\u2026',
                    'Spotting repeat offenders\u2026',
                    'Plating the patterns\u2026',
                  ]
            }
          />
        </div>
      )}

      {phase === 'error' && (
        <div className="mt-2 text-sm text-red-700">
          {error}
          <button
            type="button"
            onClick={generate}
            className="mt-2 ml-1 rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
          >
            Try again
          </button>
        </div>
      )}

      {phase === 'ready' && result && (
        <>
          {stale && (
            <div className="mt-2 rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
              You've logged new waste since this ran — tap Refresh to recompute.
            </div>
          )}
          <div className={`mt-2 text-base font-medium leading-snug ${tone.headline}`}>
            {result.headline}
          </div>

          {result.patterns?.length > 0 && (
            <ul className="mt-3 space-y-2.5">
              {result.patterns.map((p, i) => (
                <li
                  key={i}
                  className="rounded-lg bg-white/80 px-3 py-2.5 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium text-black/85">
                      {p.title}
                    </div>
                    {p.dollar_impact != null && (
                      <div className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium tabular-nums text-red-700">
                        ~{formatMoney(p.dollar_impact)}
                      </div>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-black/70 leading-snug">
                    {p.detail}
                  </div>
                  {p.recommendation && (
                    <div className="mt-2 rounded-md bg-blue-50 px-2.5 py-1.5 text-xs">
                      <span className="font-medium text-blue-700">Try:</span>
                      <span className="ml-1 text-black/80">{p.recommendation}</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {result.summary_action && result.summary_action.trim() && (
            <div className={`mt-3 rounded-md px-3 py-2 text-sm ${result._too_thin ? 'bg-blue-50' : 'bg-white/70'}`}>
              <span className={`text-[11px] font-medium ${tone.label}`}>
                {result._too_thin ? 'Tip' : 'Top action'}
              </span>
              <span className="ml-2 text-black/85">{result.summary_action}</span>
            </div>
          )}
        </>
      )}
    </section>
  )
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100
}

const TONES = {
  positive: {
    border: 'border-emerald-200',
    bg:     'bg-emerald-50/60',
    label:  'text-emerald-900/70',
    headline: 'text-emerald-900',
  },
  neutral: {
    border: 'border-blue-200',
    bg:     'bg-blue-50/40',
    label:  'text-blue-900/70',
    headline: 'text-blue-900',
  },
  concerning: {
    border: 'border-red-200',
    bg:     'bg-red-50/40',
    label:  'text-red-900/70',
    headline: 'text-red-900',
  },
}
